import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import type { JSX } from 'react';

export type SerializedTemplateVariableNode = Spread<
  {
    variableKey: string;
    displayValue: string;
  },
  SerializedLexicalNode
>;

function TemplateVariableChip({
  variableKey,
  displayValue,
}: {
  variableKey: string;
  displayValue: string;
}) {
  return (
    <span
      className="lexical-tpl-var"
      data-tpl-var={variableKey}
      title={variableKey}
      contentEditable={false}
    >
      {displayValue}
    </span>
  );
}

function $convertTemplateVariableElement(
  domNode: HTMLElement,
): DOMConversionOutput | null {
  const variableKey = domNode.getAttribute('data-tpl-var');
  if (!variableKey) return null;
  const displayValue = domNode.textContent ?? '';
  return {
    node: $createTemplateVariableNode(variableKey, displayValue),
  };
}

export class TemplateVariableNode extends DecoratorNode<JSX.Element> {
  __variableKey: string;
  __displayValue: string;

  static getType(): string {
    return 'template-variable';
  }

  static clone(node: TemplateVariableNode): TemplateVariableNode {
    return new TemplateVariableNode(
      node.__variableKey,
      node.__displayValue,
      node.__key,
    );
  }

  constructor(variableKey: string, displayValue: string, key?: NodeKey) {
    super(key);
    this.__variableKey = variableKey;
    this.__displayValue = displayValue;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  static importJSON(
    serializedNode: SerializedTemplateVariableNode,
  ): TemplateVariableNode {
    return $createTemplateVariableNode(
      serializedNode.variableKey,
      serializedNode.displayValue,
    );
  }

  exportJSON(): SerializedTemplateVariableNode {
    return {
      ...super.exportJSON(),
      variableKey: this.__variableKey,
      displayValue: this.__displayValue,
      type: 'template-variable',
      version: 1,
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      // Priority 2, not 1: ExtendedTextNode bumps *every* built-in TextNode
      // conversion (including `span`) to priority 1, and Lexical resolves a
      // priority tie in favour of the later-registered node. At priority 1 the
      // marker span imported as plain text — the chip vanished and the email
      // body kept a hardcoded value that no longer tracked the invoice list.
      // Guarded by TemplateVariableNode.test.tsx.
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-tpl-var')) return null;
        return {
          conversion: $convertTemplateVariableElement,
          priority: 2,
        };
      },
    };
  }

  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-tpl-var', this.__variableKey);
    element.textContent = this.__displayValue;
    return { element };
  }

  getVariableKey(): string {
    return this.__variableKey;
  }

  getDisplayValue(): string {
    const self = this.getLatest();
    return self.__displayValue;
  }

  setDisplayValue(value: string): void {
    const self = this.getWritable();
    self.__displayValue = value;
  }

  decorate(_editor: LexicalEditor, _config: EditorConfig): JSX.Element {
    return (
      <TemplateVariableChip
        variableKey={this.__variableKey}
        displayValue={this.__displayValue}
      />
    );
  }
}

export function $createTemplateVariableNode(
  variableKey: string,
  displayValue: string,
): TemplateVariableNode {
  return $applyNodeReplacement(
    new TemplateVariableNode(variableKey, displayValue),
  );
}

export function $isTemplateVariableNode(
  node: LexicalNode | null | undefined,
): node is TemplateVariableNode {
  return node instanceof TemplateVariableNode;
}

/**
 * Convert marker spans back to {{var}} mustache placeholders for the
 * backend send path. Call this on the HTML from the editor before sending.
 */
export function templateVariableMarkersToMustache(html: string): string {
  return html.replace(
    /<span data-tpl-var="(\w+)">[^<]*<\/span>/g,
    (_match, key: string) => `{{${key}}}`,
  );
}
